import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Cell, Grid, Row, Button, useOverlay } from '@fs/zion-ui'
import { useParams } from '@fs/zion-router'
import { usePageView } from '@fs/zion-analytics'
import { usePageTitle } from '../../../app/PageTitleContext'
import { RawData } from '../../../common/RawData'
import DmcPaginator from '../../../common/DmcPaginator'
import useCompactMessagesListController from './useCompactMessagesListController'
import CompactMessagesTableInfo from './CompactMessagesTableInfo'
import CompactMessagesListTableFilters from './CompactMessagesListTableFilters'
import QueueColumnsDialogOverlay from './QueueColumnsDialogOverlay'
import CompactMessagesListTable from './CompactMessagesListTable'
import CompactMessagesContextMenu from './CompactMessagesContextMenu'
import TreeCategories from './TreeCategories'
import { getBackendBaseUrl } from '../../../../util/UrlUtil'
import useStreamDownload from '../../../../hooks/useStreamDownload'

export default function CompactMessagesList({ queueState, queueActions, queueCachesState }) {
  usePageView({ page_detail: `DMConsole:CompactMessagesList:${useParams().queuesFilter}` })
  const { t } = useTranslation()
  usePageTitle(t('pageTitle.queues.compactMessagesList'))
  const filterRef = useRef()
  const { compactQueueState, compactQueueActions } = useCompactMessagesListController({
    queueState,
    queueActions,
    queueCachesState,
    filterRef,
  })

  const queueColumnsOverlay = useOverlay()
  const { streamDataDownload } = useStreamDownload()

  const handleDownloadMessages = () => {
    const treeCategories = [...queueState.treeCategories]
    queueState.treeCategories.includes('FAMILY_TREE') && treeCategories.push('SHARED_FAMILY_GROUP')
    streamDataDownload({
      requestPath: `${getBackendBaseUrl()}/dmqueues/${encodeURIComponent(queueState.queueName)}/messages/dump`,
      bodyFields: {
        query: queueState.query,
        sortDirection: queueState.sortDirection,
        sortField: queueState.sortField,
        treeCategories,
      },
    })
  }

  return (
    <>
      <Grid>
        {/* Row */}
        <CompactMessagesTableInfo queueState={queueState} queueActions={queueActions} />
        <TreeCategories setQueueState={queueActions.setQueueState} />
        {/* Row */}
        <Row wrap>
          <CompactMessagesListTableFilters
            queueState={queueState}
            queueCachesState={queueCachesState}
            queueActions={queueActions}
            filterRef={filterRef}
          />
          <Button onClick={handleDownloadMessages}>{t('queues.downloadMessages', 'Download Messages')}</Button>
          <div growX />
          <Button emphasis="medium" onClick={queueColumnsOverlay.handleClick}>
            {t('queues.displayColumns', 'Display Columns')}
          </Button>
          <QueueColumnsDialogOverlay
            queueColumns={compactQueueState.queueColumns}
            activeColumns={compactQueueState.activeColumns?.columnList}
            onColumnChange={compactQueueActions.handleColumnCheckboxChange}
            {...queueColumnsOverlay}
          />
        </Row>
        <Cell columns={12}>
          <DmcPaginator
            id="compactQueueTablePaginator"
            index={queueState.queuePageIndex}
            setIndex={queueActions.setQueuePageIndex}
            maxPageCount={500}
            pageCount={queueState.pageLimit <= 500 ? queueState.pageLimit : 500}
            length={queueState.queueTotal}
            topAndBottom
            countKey="DMQueuePaginator"
            setPageCount={queueActions.setPageLimit}
          >
            <RawData state={queueState} testId="queueState" />
            {compactQueueState.activeColumns && (
              <CompactMessagesListTable
                queueState={queueState}
                compactQueueState={compactQueueState}
                queueActions={queueActions}
                compactQueueActions={compactQueueActions}
              />
            )}
          </DmcPaginator>
        </Cell>
      </Grid>
      {compactQueueState.contextMenu && (
        <CompactMessagesContextMenu
          queueState={queueState}
          compactQueueState={compactQueueState}
          queueActions={queueActions}
        />
      )}
    </>
  )
}
